import React, { useState, useContext, Fragment } from 'react';
import { NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import { defaultNavOptions } from './defaultNavOptions';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { MainColors } from '../constants/Colors';
import * as Linking from "expo-linking";
import { View, Text, StyleSheet, TouchableOpacity, useWindowDimensions } from 'react-native';
import DrawerContent from './DrawerContent';
import Drawer from 'react-native-drawer';
import { AuthContext } from '../context/AuthProvider';
import "@expo/match-media";
import { useMediaQuery } from "react-responsive";

// Auth Screens
import LoginScreen, { screenOptions as LoginScreenOptions } from '../screens/Auth/LoginScreen';
import ForgotPasswordScreen, { screenOptions as ForgotPasswordScreenOptions } from '../screens/Auth/ForgotPasswordScreen';
import ActivateScreen, { screenOptions as ActivateScreenOptions } from '../screens/Auth/ActivateScreen';

// User Screens
import BallotsScreen from '../screens/Ballots/BallotsScreen';
import BallotDetailsScreen, { screenOptions as BallotDetailsScreenOptions } from '../screens/Ballots/BallotDetailsScreen';
import VoteScreen, { screenOptions as VoteScreenOptions } from '../screens/Ballots/VoteScreen';

import ResultsScreen from '../screens/Results/ResultsScreen';
import BallotResultsRundownScreen, { screenOptions as BallotResultsRundownOptions } from '../screens/Results/BallotResultsRundownScreen';

// Admin Ballots Screens
import AdminBallotListScreen from '../screens/Admin/Ballots/AdminBallotListScreen';
import BallotFormScreen from '../screens/Admin/Ballots/BallotFormScreen';

// Admin Users Screens
import UsersScreen from '../screens/Admin/Users/UsersScreen';
import CreateUserScreen from '../screens/Admin/Users/CreateUserScreen';
import EditUserScreen from '../screens/Admin/Users/EditUserScreen';

// Super Duper Admin Screens
import OrganizationsScreen from '../screens/Admin/Organizations/OrganizationsScreen';
import CreateOrganizationScreen from '../screens/Admin/Organizations/CreateOrganizationScreen';
import EditOrganizationScreen from '../screens/Admin/Organizations/EditOrganizationScreen';

// Settings Screens
import SettingsScreen from '../screens/Settings/SettingsScreen';

const withLayout = (Component) => {
    const { isLoggedIn } = useContext(AuthContext);

    const [drawerOpen, setDrawerOpen] = useState(false)

    const WrappedWithLayout = ({ navigation, ...props }) => {
        const drawerContent = <DrawerContent navigation={navigation} setDrawerOpen={setDrawerOpen} />

        const isDesktop = useMediaQuery({
            query: "(min-width: 768px)",
        });

        const mainComponent = <Component navigation={navigation} {...props} />

        const windowHeight = useWindowDimensions().height;
        const topBarHeight = 64;

        /*overflow hidden gets rid of the scroll bar popping out causing a "twitchy effect" */
        return <View style={{ flex: 1, overflow: 'hidden' }}>
            <View style={styles.container}>
                {isDesktop ?
                    <Fragment>
                        <View style={{ height: windowHeight - topBarHeight, flex: 1, flexDirection: 'row' }}>
                            <View>{drawerContent}</View>
                            <View style={{ flex: 1 }}>{mainComponent}</View>
                        </View>
                    </Fragment>
                    :
                    <Drawer
                        content={drawerContent}
                        open={drawerOpen}
                        type="overlay"
                        styles={drawerStyles}
                        openDrawerOffset={0.3}
                        elevation={10}
                        captureGestures={false}>

                        {mainComponent}

                        { /* This is the mask for the drawer */}
                        {drawerOpen ? <TouchableOpacity style={styles.mask} onPress={() => setDrawerOpen(false)} /> : false}
                    </Drawer>
                }
            </View>
            {isLoggedIn && !isDesktop &&
                <View style={styles.bottomBar}>
                    <MenuItem iconName="menu" label="Menu" onPress={() => setDrawerOpen(d => !d)} />
                    <MenuItem iconName="vote" label="Ballots" onPress={() => {
                        navigation.navigate('BallotsDashboard')
                    }} />
                    <MenuItem iconName="clipboard-text" label="Results" onPress={() => {
                        navigation.navigate('ResultsDashboard')
                    }} />
                </View>
            }
        </View>
    }

    return WrappedWithLayout
}

const MenuItem = ({ iconName, label, onPress }) => {
    return <TouchableOpacity onPress={onPress} style={{ alignItems: 'center', padding: 4 }}>
        <MaterialCommunityIcons name={iconName} size={24} color='#fff' />
        <Text style={{ color: '#fff', fontSize: 12 }}>{label}</Text>
    </TouchableOpacity>
}

const Stack = createStackNavigator();

const AuthStackNavigator = () => {
    return (
        <Stack.Navigator screenOptions={defaultNavOptions}>
            <Stack.Screen name="LoginScreen" component={LoginScreen}
                options={LoginScreenOptions} />
            <Stack.Screen name="ForgotPasswordScreen" component={ForgotPasswordScreen}
                options={ForgotPasswordScreenOptions} />
            <Stack.Screen name="ActivateScreen" component={ActivateScreen}
                options={ActivateScreenOptions} />
        </Stack.Navigator>
    )
}

const StackNavigator = () => {
    return (
        <Stack.Navigator screenOptions={defaultNavOptions}>
            {/* Main - Ballots */}
            <Stack.Screen name="BallotsDashboard" component={withLayout(BallotsScreen)}
                options={{ headerTitle: 'Ballots' }} />
            <Stack.Screen name="BallotDetails" component={withLayout(BallotDetailsScreen)}
                options={BallotDetailsScreenOptions} />
            <Stack.Screen name="Vote" component={withLayout(VoteScreen)} options={VoteScreenOptions} />

            {/* Main - Results */}
            <Stack.Screen name="ResultsDashboard" component={withLayout(ResultsScreen)}
                options={{ headerTitle: 'Results' }}
            />
            <Stack.Screen name="BallotResultsRundown" component={withLayout(BallotResultsRundownScreen)}
                options={BallotResultsRundownOptions} />

            {/* Admin */}
            <Stack.Screen name="AdminBallotsDashboard" component={withLayout(AdminBallotListScreen)} />
            <Stack.Screen name="AdminBallotForm" component={withLayout(BallotFormScreen)} />

            {/* Users */}
            <Stack.Screen name="AdminUsersDashboard" component={withLayout(UsersScreen)} />
            <Stack.Screen name="AdminCreateUser" component={withLayout(CreateUserScreen)} />
            <Stack.Screen name="AdminEditUser" component={withLayout(EditUserScreen)} />

            {/* Organizations */}
            <Stack.Screen name="SuperDuperAdminOrganizationsDashboard"
                component={withLayout(OrganizationsScreen)} />
            <Stack.Screen name="SuperDuperAdminCreateOrganization"
                component={withLayout(CreateOrganizationScreen)} />
            <Stack.Screen name="SuperDuperAdminEditOrganization"
                component={withLayout(EditOrganizationScreen)} />

            {/* Settings */}
            <Stack.Screen name="SettingsDashboard" component={withLayout(SettingsScreen)} />
        </Stack.Navigator>)
}

const config = {
    screens: {

        // Logged Out
        LoginScreen: 'account/login',
        ForgotPasswordScreen: 'account/forgotpassword',
        ActivateScreen: 'account/activate',

        // Main
        BallotsDashboard: 'ballots/dashboard',
        BallotDetails: 'ballots/dashboard/ballotDetails',
        Vote: 'ballots/dashboard/ballotDetails/vote',
        ResultsDashboard: 'results/dashboard',
        BallotResultsRundown: 'results/dashboard/rundown',

        // Admin Ballots
        AdminBallotsDashboard: 'admin/ballots/dashboard',
        AdminCreateBallot: 'admin/ballots/create',
        AdminEditBallot: 'admin/ballots/edit',
        AdminNewBallotItem: 'admin/ballots/create/newBallotItem',
        AdminEditBallotItem: 'admin/ballots/create/editBallotItem',

        // Admin Users
        AdminUsersDashboard: 'admin/users/dashboard',
        AdminCreateUser: 'admin/users/create',
        AdminEditUser: 'admin/user/edit',

        // Super Duper Admin Organizations
        SuperDuperAdminOrganizationsDashboard: 'admin/superDuperAdmin/organizations/dashboard',
        SuperDuperAdminCreateOrganization: 'admin/superDuperAdmin/organizations/create',
        SuperDuperAdminEditOrganization: 'admin/superDuperAdmin/organizations/edit',

        AdminBallotForm: 'admin/ballots/createOrEdit',

        // Settings
        SettingsDashboard: 'settings/dashboard'
    },
};

const linking = {
    prefixes: [Linking.makeUrl("/")],
    config,
};

const AppNavigator = ({ }) => {
    const { isLoggedIn } = useContext(AuthContext);

    return (
        <NavigationContainer linking={linking} documentTitle={{ formatter: (options, route) => 'votl.io'}} >
            {isLoggedIn ? <StackNavigator /> : <AuthStackNavigator />}
        </NavigationContainer>
    );
}

const drawerStyles = {
    drawer: {
        backgroundColor: '#fff',
        elevation: 5,
        zIndex: 5,
    },
    container: {
        backgroundColor: "black"
    },
    main: {
        backgroundColor: '#fff',
        zIndex: 1
    }
};
const styles = StyleSheet.create({
    container: {
        flex: 1,
        flexDirection: 'row',
        backgroundColor: 'white'
    },
    bottomBar: {
        backgroundColor: MainColors.darkGray,
        padding: 5,
        justifyContent: 'space-around',
        alignItems: 'center',
        flexDirection: 'row',
        padding: 5,
    },
    mask: {
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: '#000',
        opacity: 0.5,
        zIndex: 0
    }
})
export default AppNavigator;